import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import { calcularINSSBreakdown } from "../../calculadora-comparativa/utils/comparativeFunctions";
import type { CustoFuncionarioResultsType } from "../calculadora-custo-funcionario";
import {
  EXPLICACOES_CAMPOS,
  REGIMES_EMPRESA_2025,
} from "../calculadora-custo-funcionario";

const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace(".", ",");
};

const Tooltip = ({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-normal z-20 text-center">
      {text}
    </div>
  </div>
);

const INSSBreakdown = ({ salario }: { salario: number }) => {
  const breakdown = calcularINSSBreakdown(salario);

  type FaixaINSS = {
    limite: number;
    aliquota: number;
    valorFaixa: number;
    contribuicaoFaixa: number;
    limiteInicial: number;
    limiteFinal: number;
  };

  return (
    <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 text-xs">
      <h5 className="font-semibold text-blue-800 mb-2">
        Cálculo INSS por Faixas:
      </h5>
      {breakdown.faixas.map((item: FaixaINSS, index: number) => (
        <p key={index} className="text-blue-700 mb-1">
          • {item.aliquota.toFixed(1)}% sobre R${" "}
          {item.valorFaixa.toFixed(2).replace(".", ",")} = R${" "}
          {item.contribuicaoFaixa.toFixed(2).replace(".", ",")}
        </p>
      ))}
      <div className="border-t border-blue-300 mt-2 pt-2">
        <p className="font-semibold text-blue-800">
          Total INSS: R$ {breakdown.valorTotal.toFixed(2).replace(".", ",")}
        </p>
      </div>
    </div>
  );
};

const IRRFBreakdown = ({ breakdown }: { breakdown: any }) => {
  return (
    <div className="mt-2 p-3 bg-orange-50 rounded border border-orange-200 text-xs">
      <h5 className="font-semibold text-orange-800 mb-2">Cálculo IRRF:</h5>
      <p className="text-orange-700">
        • Salário bruto: R$ {breakdown.salario.toFixed(2).replace(".", ",")}
      </p>

      {/* CORREÇÃO: Verificar isenção para dois salários mínimos */}
      {breakdown.salario <= 3036.0 ? (
        <div className="mt-2 p-2 bg-green-100 rounded border border-green-300">
          <p className="text-green-800 font-semibold">
            ✓ ISENTO - Lei nº 15.191/2025
          </p>
          <p className="text-green-700 text-xs">
            Isenção de IRRF para quem ganha até R$ 3.036,00 (dois salários
            mínimos)
          </p>
        </div>
      ) : (
        <>
          <p className="text-orange-700">
            • (-) INSS: R$ {breakdown.inss.toFixed(2).replace(".", ",")}
          </p>
          <p className="text-orange-700 font-medium">
            • = Base de cálculo: R${" "}
            {breakdown.baseCalculo.toFixed(2).replace(".", ",")}
          </p>

          {breakdown.faixaAplicada.aliquota > 0 ? (
            <>
              <p className="text-orange-700 mt-1">
                • Faixa aplicada:{" "}
                {(breakdown.faixaAplicada.aliquota * 100).toFixed(1)}%
              </p>
              <p className="text-orange-700">
                • Cálculo: R${" "}
                {breakdown.baseCalculo.toFixed(2).replace(".", ",")} ×{" "}
                {(breakdown.faixaAplicada.aliquota * 100).toFixed(1)}% - R${" "}
                {breakdown.faixaAplicada.parcelaDeducao
                  .toFixed(2)
                  .replace(".", ",")}
              </p>
            </>
          ) : (
            <p className="text-orange-700 mt-1 font-medium">
              • Faixa: Isento (até R$ 2.428,80)
            </p>
          )}
        </>
      )}

      <div className="border-t border-orange-300 mt-2 pt-2">
        <p className="font-semibold text-orange-800">
          Total IRRF: R$ {breakdown.valorIRRF.toFixed(2).replace(".", ",")}
        </p>
      </div>
    </div>
  );
};

const ValeTransporteBreakdown = ({ breakdown }: { breakdown: any }) => {
  return (
    <div className="mt-2 p-3 bg-purple-50 rounded border border-purple-200 text-xs">
      <h5 className="font-semibold text-purple-800 mb-2">
        Cálculo Vale Transporte:
      </h5>
      <p className="text-purple-700">
        • Valor total do vale: R${" "}
        {breakdown.valorTotal.toFixed(2).replace(".", ",")}
      </p>
      <p className="text-purple-700">
        • Limite desconto (6% salário): R${" "}
        {breakdown.limiteSalarial.toFixed(2).replace(".", ",")}
      </p>
      <p className="text-purple-700">
        • Desconto do funcionário: R${" "}
        {breakdown.descontoFuncionario.toFixed(2).replace(".", ",")}
      </p>
      <div className="border-t border-purple-300 mt-2 pt-2">
        <p className="font-semibold text-purple-800">
          Contribuição empresa: R${" "}
          {breakdown.custoEmpresa.toFixed(2).replace(".", ",")}
        </p>
        <p className="text-xs text-purple-600 mt-1">
          (Apenas valor que excede desconto do funcionário)
        </p>
      </div>
    </div>
  );
};

const EncargosBreakdown = ({
  regime,
  salarioBruto,
}: {
  regime: "simples" | "lucro";
  salarioBruto: number;
}) => {
  const regimeConfig = REGIMES_EMPRESA_2025[regime];

  return (
    <div className="mt-2 p-3 bg-green-50 rounded border border-green-200 text-xs">
      <h5 className="font-semibold text-green-800 mb-2">
        Encargos Patronais - {regimeConfig.nome}:
      </h5>

      {regime === "simples" ? (
        <>
          <p className="text-green-700">
            • INSS Patronal: <strong>ISENTO</strong> (economia de 20%)
          </p>
          <p className="text-green-700">
            • Salário Educação: <strong>ISENTO</strong> (economia de 2,5%)
          </p>
          <p className="text-green-700">
            • Sistema S: <strong>ISENTO</strong> (economia de 3,3%)
          </p>
          <p className="text-green-700">
            • Seguro Acidente: <strong>ISENTO</strong> (economia de 2%)
          </p>
          <p className="text-green-700">
            • FGTS: R${" "}
            {(salarioBruto * regimeConfig.fgts).toFixed(2).replace(".", ",")}{" "}
            (8%)
          </p>
          <div className="border-t border-green-300 mt-2 pt-2">
            <p className="font-semibold text-green-800">
              Total encargos: R${" "}
              {(salarioBruto * regimeConfig.totalEncargos)
                .toFixed(2)
                .replace(".", ",")}{" "}
              (8%)
            </p>
            <p className="text-xs text-green-600 mt-1">
              <strong>Economia vs Lucro Presumido:</strong> R${" "}
              {(
                salarioBruto *
                (REGIMES_EMPRESA_2025.lucro.totalEncargos -
                  regimeConfig.totalEncargos)
              )
                .toFixed(2)
                .replace(".", ",")}{" "}
              (27,8%)
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="text-green-700">
            • INSS Patronal: R${" "}
            {(salarioBruto * regimeConfig.inssPatronal)
              .toFixed(2)
              .replace(".", ",")}{" "}
            (20%)
          </p>
          <p className="text-green-700">
            • Salário Educação: R${" "}
            {(salarioBruto * regimeConfig.salarioEducacao)
              .toFixed(2)
              .replace(".", ",")}{" "}
            (2,5%)
          </p>
          <p className="text-green-700">
            • Sistema S: R${" "}
            {(salarioBruto * regimeConfig.sistemaS)
              .toFixed(2)
              .replace(".", ",")}{" "}
            (3,3%)
          </p>
          <p className="text-green-700">
            • Seguro Acidente: R${" "}
            {(salarioBruto * regimeConfig.seguroAcidente)
              .toFixed(2)
              .replace(".", ",")}{" "}
            (2%)
          </p>
          <p className="text-green-700">
            • FGTS: R${" "}
            {(salarioBruto * regimeConfig.fgts).toFixed(2).replace(".", ",")}{" "}
            (8%)
          </p>
          <div className="border-t border-green-300 mt-2 pt-2">
            <p className="font-semibold text-green-800">
              Total encargos: R${" "}
              {(salarioBruto * regimeConfig.totalEncargos)
                .toFixed(2)
                .replace(".", ",")}{" "}
              (35,8%)
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Componente principal
export const CustoFuncionarioResponse = ({
  results,
}: {
  results: CustoFuncionarioResultsType;
}) => {
  const { custoMensal, custoAnual, comparativo, breakdown, regime } = results;

  const [showINSSDetail, setShowINSSDetail] = useState(false);
  const [showIRRFDetail, setShowIRRFDetail] = useState(false);
  const [showValeTransporteDetail, setShowValeTransporteDetail] =
    useState(false);
  const [showEncargosDetail, setShowEncargosDetail] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado do Custo de Funcionário CLT</MainCardTitle>

      {/* Cards de Resumo Principal - 2 CARDS CENTRALIZADOS */}
      <div className="grid sm:grid-cols-2 gap-6 mb-6 grid-cols-1 max-w-4xl mx-auto">
        <div className="p-4 bg-primary rounded border flex flex-col justify-center">
          <Tooltip text="Custo total mensal: salário + encargos + benefícios + provisões mensais (13º, férias, FGTS adicional e multa FGTS rateados)">
            <h2 className="sm:text-lg text-base text-center text-white font-semibold m-0 cursor-help">
              Custo Total Mensal
            </h2>
          </Tooltip>
          <p className="sm:text-2xl text-xl text-center font-bold text-white m-0">
            <span className="text-base mr-1 text-white">R$</span>
            {normalSanatizerBRL(custoMensal.custoTotalMensal)}
          </p>
          <p className="text-sm text-center text-white opacity-90 mt-1">
            Incluindo todas as provisões rateadas
          </p>
        </div>

        <div className="p-4 bg-white rounded border flex flex-col justify-center border-2 border-red-200">
          <Tooltip text="Custo total anual: custo mensal × 12 (já inclui todas as provisões rateadas mensalmente)">
            <h2 className="sm:text-lg text-base text-center font-semibold m-0 cursor-help">
              Custo Total Anual
            </h2>
          </Tooltip>
          <p className="sm:text-2xl text-xl text-center font-black text-red-800 m-0">
            <span className="text-base mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(custoAnual.custoTotalAnual)}
          </p>
          <p className="text-sm text-center text-gray-600 mt-1">
            Custo mensal × 12 meses
          </p>
        </div>
      </div>

      {/* Destaque do Regime */}
      <div className="mb-6 p-4 bg-gray-50 rounded border text-center">
        <p className="text-sm text-gray-600 mb-1">
          <strong>Regime atual:</strong> {REGIMES_EMPRESA_2025[regime].nome}
        </p>
        <p className="text-sm text-gray-600">
          <strong>Encargos patronais:</strong>{" "}
          {formatarMoedaPtBR(REGIMES_EMPRESA_2025[regime].totalEncargos * 100)}%
          da folha
        </p>
        {regime === "simples" && (
          <p className="text-sm text-green-600 font-medium mt-1">
            Economia de 7,8% em encargos vs Lucro Presumido
          </p>
        )}
      </div>

      {/* CORREÇÃO: Alert sobre isenção de IRRF */}
      {custoMensal.salarioBruto <= 3036.0 && (
        <div className="mb-6 p-4 bg-green-50 rounded border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-green-600 font-bold">✓</span>
            <h3 className="font-semibold text-green-800">
              IRRF Isento - Lei nº 15.191/2025
            </h3>
          </div>
          <p className="text-sm text-green-700">
            Este funcionário está <strong>isento de IRRF</strong> por ganhar até
            R$ 3.036,00 (dois salários mínimos de 2025). A isenção foi ampliada
            em agosto de 2025.
          </p>
        </div>
      )}

      {/* Metodologia */}
      <div className="mb-6">
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          className="w-full p-3 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 text-left transition-colors duration-200"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-800 text-sm">
              Como Chegamos Nestes Valores
            </h3>
            <span className="text-blue-600 text-sm">
              {showMethodology ? "Ocultar" : "Ver Metodologia"}
            </span>
          </div>
        </button>

        {showMethodology && (
          <div className="mt-3 p-4 bg-blue-50 rounded border border-blue-200">
            <div className="grid sm:grid-cols-2 gap-4 text-xs">
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">
                  Custo Mensal Real - Composição:
                </h4>
                <p className="text-blue-700 mb-1">
                  1. Salário bruto: R${" "}
                  {normalSanatizerBRL(custoMensal.salarioBruto)}
                </p>
                <p className="text-blue-700 mb-1">
                  2. + Encargos patronais: R${" "}
                  {normalSanatizerBRL(custoMensal.encargosPagos.totalEncargos)}
                </p>
                <p className="text-blue-700 mb-1">
                  3. + Benefícios: R${" "}
                  {normalSanatizerBRL(
                    custoMensal.beneficiosPagos.totalBeneficios,
                  )}
                </p>
                <p className="text-blue-700 mb-1">
                  4. + Provisões mensais obrigatórias:
                </p>
                <p className="text-blue-700 mb-1">
                  {" "}
                  - 13º (1/12): R${" "}
                  {normalSanatizerBRL(
                    (custoAnual.provisoes.decimoTerceiro +
                      custoAnual.provisoes.decimoTerceiroEncargos) /
                      12,
                  )}
                </p>
                <p className="text-blue-700 mb-1">
                  {" "}
                  - Férias 1/3 (1/12): R${" "}
                  {normalSanatizerBRL(
                    (custoAnual.provisoes.feriasLiquidas +
                      custoAnual.provisoes.feriasEncargos) /
                      12,
                  )}
                </p>
                <p className="text-blue-700 mb-1">
                  {" "}
                  - FGTS adicional (1/12): R${" "}
                  {normalSanatizerBRL(
                    (custoAnual.provisoes.fgtsAnual -
                      custoMensal.salarioBruto * 12 * 0.08) /
                      12,
                  )}
                </p>
                <p className="text-blue-700 mb-1">
                  {" "}
                  - Multa FGTS (1/12): R${" "}
                  {normalSanatizerBRL(
                    custoAnual.provisoes.provisaoMultaFGTS / 12,
                  )}
                </p>
                <p className="text-blue-700 font-semibold">
                  = Custo mensal real: R${" "}
                  {normalSanatizerBRL(custoMensal.custoTotalMensal)}
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-blue-800 mb-2">
                  Custo Anual - Composição:
                </h4>
                <p className="text-blue-700 mb-2 font-medium">
                  Custo mensal × 12: R${" "}
                  {normalSanatizerBRL(custoAnual.custoTotalAnual)}
                </p>

                <h5 className="font-semibold text-blue-800 mb-1 text-xs">
                  Detalhamento FGTS anual:
                </h5>
                <p className="text-blue-700 text-xs mb-1">
                  • FGTS mensal (12 meses): R${" "}
                  {normalSanatizerBRL(custoMensal.salarioBruto * 12 * 0.08)}
                </p>
                <p className="text-blue-700 text-xs mb-1">
                  • FGTS 13º + férias: R${" "}
                  {normalSanatizerBRL(
                    custoAnual.provisoes.fgtsAnual -
                      custoMensal.salarioBruto * 12 * 0.08,
                  )}
                </p>
                <p className="text-blue-700 text-xs mb-2 font-medium">
                  = Total FGTS: R${" "}
                  {normalSanatizerBRL(custoAnual.provisoes.fgtsAnual)}
                </p>

                <h5 className="font-semibold text-blue-800 mb-1 text-xs">
                  Provisões já incluídas no custo mensal:
                </h5>
                <p className="text-blue-700 text-xs mb-1">
                  • 13º salário: R${" "}
                  {normalSanatizerBRL(
                    custoAnual.provisoes.decimoTerceiro +
                      custoAnual.provisoes.decimoTerceiroEncargos,
                  )}
                </p>
                <p className="text-blue-700 text-xs mb-1">
                  • Férias 1/3: R${" "}
                  {normalSanatizerBRL(
                    custoAnual.provisoes.feriasLiquidas +
                      custoAnual.provisoes.feriasEncargos,
                  )}
                </p>
                <p className="text-blue-700 text-xs">
                  • Multa FGTS: R${" "}
                  {normalSanatizerBRL(custoAnual.provisoes.provisaoMultaFGTS)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detalhamento Custo Mensal */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Detalhamento Custo Mensal:
      </h2>
      <div className="border rounded p-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-4 grid-cols-1">
          <div className="p-3 bg-gray-50 rounded border">
            <Tooltip text="Renda total efetiva: salário líquido + benefícios não tributáveis (vale alimentação, plano saúde, outros)">
              <h3 className="text-sm font-semibold text-center mb-1 cursor-help">
                Renda Total Efetiva
              </h3>
            </Tooltip>
            <p className="text-lg font-bold text-center text-gray-800">
              R$ {normalSanatizerBRL(custoMensal.salarioLiquido)}
            </p>
            <p className="text-xs text-center text-gray-600">
              Salário + benefícios
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded border">
            <Tooltip text="Total de encargos patronais que a empresa paga sobre o salário">
              <h3 className="text-sm font-semibold text-center mb-1 cursor-help">
                Encargos Patronais
              </h3>
            </Tooltip>
            <p className="text-lg font-bold text-center text-gray-800">
              R$ {normalSanatizerBRL(custoMensal.encargosPagos.totalEncargos)}
            </p>
            <p className="text-xs text-center text-gray-600">
              {formatarMoedaPtBR(
                REGIMES_EMPRESA_2025[regime].totalEncargos * 100,
              )}
              % do salário
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Descontos do Funcionário:
            </h4>

            {/* INSS com detalhamento */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  INSS: R${" "}
                  {normalSanatizerBRL(custoMensal.descontosFuncionario.inss)}
                </span>
                <button
                  onClick={() => setShowINSSDetail(!showINSSDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showINSSDetail ? "▼" : "Detalhes"}
                </button>
              </div>
              {showINSSDetail && (
                <INSSBreakdown salario={custoMensal.salarioBruto} />
              )}
            </div>

            {/* IRRF com detalhamento */}
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  IRRF: R${" "}
                  {normalSanatizerBRL(custoMensal.descontosFuncionario.irrf)}
                  {custoMensal.salarioBruto <= 3036.0 && (
                    <span className="text-green-600 ml-1">✓ Isento</span>
                  )}
                </span>
                <button
                  onClick={() => setShowIRRFDetail(!showIRRFDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showIRRFDetail ? "▼" : "Detalhes"}
                </button>
              </div>
              {showIRRFDetail && (
                <IRRFBreakdown breakdown={breakdown.irrfBreakdown} />
              )}
            </div>

            {/* Vale Transporte com detalhamento */}
            {custoMensal.descontosFuncionario.valeTransporte > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Vale Transporte: R${" "}
                    {normalSanatizerBRL(
                      custoMensal.descontosFuncionario.valeTransporte,
                    )}
                  </span>
                  <button
                    onClick={() =>
                      setShowValeTransporteDetail(!showValeTransporteDetail)
                    }
                    className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    {showValeTransporteDetail ? "▼" : "Detalhes"}
                  </button>
                </div>
                {showValeTransporteDetail && (
                  <ValeTransporteBreakdown
                    breakdown={breakdown.valeTransporteBreakdown}
                  />
                )}
              </div>
            )}
          </div>

          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Encargos Empresa ({REGIMES_EMPRESA_2025[regime].nome}):
            </h4>

            <div className="mb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Total Encargos: R${" "}
                  {normalSanatizerBRL(custoMensal.encargosPagos.totalEncargos)}
                </span>
                <button
                  onClick={() => setShowEncargosDetail(!showEncargosDetail)}
                  className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {showEncargosDetail ? "▼" : "Detalhes"}
                </button>
              </div>
              {showEncargosDetail && (
                <EncargosBreakdown
                  regime={regime}
                  salarioBruto={custoMensal.salarioBruto}
                />
              )}
            </div>

            {custoMensal.beneficiosPagos.totalBeneficios > 0 && (
              <>
                <h5 className="text-xs font-medium text-gray-600 mt-3 mb-1">
                  Benefícios:
                </h5>
                {custoMensal.beneficiosPagos.valeTransporteEmpresa > 0 && (
                  <p className="text-sm text-gray-600 mb-1">
                    Vale Transporte: R${" "}
                    {normalSanatizerBRL(
                      custoMensal.beneficiosPagos.valeTransporteEmpresa,
                    )}
                  </p>
                )}
                {custoMensal.beneficiosPagos.valeAlimentacao > 0 && (
                  <p className="text-sm text-gray-600 mb-1">
                    Vale Alimentação: R${" "}
                    {normalSanatizerBRL(
                      custoMensal.beneficiosPagos.valeAlimentacao,
                    )}
                  </p>
                )}
                {custoMensal.beneficiosPagos.planoSaude > 0 && (
                  <p className="text-sm text-gray-600 mb-1">
                    Plano Saúde: R${" "}
                    {normalSanatizerBRL(custoMensal.beneficiosPagos.planoSaude)}
                  </p>
                )}
                {custoMensal.beneficiosPagos.outrosBeneficios > 0 && (
                  <p className="text-sm text-gray-600">
                    Outros: R${" "}
                    {normalSanatizerBRL(
                      custoMensal.beneficiosPagos.outrosBeneficios,
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detalhamento Custo Anual */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Detalhamento Custo Anual:
      </h2>
      <div className="border rounded p-4 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Provisões Anuais (já incluídas no custo mensal):
            </h4>

            <Tooltip text="13º salário líquido + encargos patronais sobre o 13º">
              <p className="text-sm text-gray-600 cursor-help mb-1">
                13º Salário: R${" "}
                {normalSanatizerBRL(
                  custoAnual.provisoes.decimoTerceiro +
                    custoAnual.provisoes.decimoTerceiroEncargos,
                )}
              </p>
            </Tooltip>

            <Tooltip text="Apenas 1/3 adicional das férias + encargos (salário normal já contabilizado mensalmente)">
              <p className="text-sm text-gray-600 cursor-help mb-1">
                Férias 1/3: R${" "}
                {normalSanatizerBRL(
                  custoAnual.provisoes.feriasLiquidas +
                    custoAnual.provisoes.feriasEncargos,
                )}
              </p>
            </Tooltip>

            <Tooltip text="Reserva de 40% sobre saldo FGTS para cobrir multa em caso de demissão sem justa causa">
              <p className="text-sm text-orange-600 cursor-help mb-2">
                Provisão Multa FGTS: R${" "}
                {normalSanatizerBRL(custoAnual.provisoes.provisaoMultaFGTS)}
              </p>
            </Tooltip>

            <div className="border-t pt-2">
              <h5 className="text-xs font-medium text-gray-700 mb-1">
                Detalhamento FGTS anual:
              </h5>
              <p className="text-xs text-gray-600 mb-1">
                • FGTS mensal (12 meses): R${" "}
                {normalSanatizerBRL(custoMensal.salarioBruto * 12 * 0.08)}
              </p>
              <p className="text-xs text-gray-600 mb-1">
                • FGTS 13º + férias: R${" "}
                {normalSanatizerBRL(
                  custoAnual.provisoes.fgtsAnual -
                    custoMensal.salarioBruto * 12 * 0.08,
                )}
              </p>
              <p className="text-xs text-gray-700 font-medium">
                = Total FGTS: R${" "}
                {normalSanatizerBRL(custoAnual.provisoes.fgtsAnual)}
              </p>
            </div>
          </div>

          <div className="p-3 bg-white rounded border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Comparativo de Regimes:
            </h4>
            <p className="text-sm text-gray-600 mb-1">
              <strong>{comparativo.regimeAtual}:</strong> R${" "}
              {normalSanatizerBRL(comparativo.custoAtual)}/ano
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{comparativo.regimeComparacao}:</strong> R${" "}
              {normalSanatizerBRL(comparativo.custoComparacao)}/ano
            </p>

            {comparativo.diferencaAnual > 0 ? (
              <p className="text-sm text-green-600 font-medium">
                Economia atual: R${" "}
                {normalSanatizerBRL(comparativo.diferencaAnual)}/ano (
                {formatarMoedaPtBR(comparativo.diferencaPercentual)}%)
              </p>
            ) : (
              <p className="text-sm text-red-600 font-medium">
                Custo adicional atual: R${" "}
                {normalSanatizerBRL(Math.abs(comparativo.diferencaAnual))}/ano
                (+{formatarMoedaPtBR(Math.abs(comparativo.diferencaPercentual))}
                %)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Recomendações */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Recomendações:
      </h2>

      {/* Alerta de provisão */}
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200">
        <Tooltip text={EXPLICACOES_CAMPOS.provisaoSalarios}>
          <h4 className="font-semibold text-yellow-800 mb-2 cursor-help">
            Provisão de Caixa Recomendada:
          </h4>
        </Tooltip>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <p className="text-yellow-700">
            <strong>3 meses:</strong> R${" "}
            {normalSanatizerBRL(custoMensal.custoTotalMensal * 3)}
          </p>
          <p className="text-yellow-700">
            <strong>6 meses:</strong> R${" "}
            {normalSanatizerBRL(custoMensal.custoTotalMensal * 6)}
          </p>
        </div>
        <p className="text-xs text-yellow-600 mt-2">
          Reserve de 3 a 6 meses de custos para cobrir períodos de transição,
          férias coletivas ou processos de demissão.
        </p>
      </div>

      {/* Disclaimer */}
      <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>Importante:</strong> Esta é uma simulação com valores
          aproximados usando tabelas tributárias 2025. Não considera custos
          operacionais adicionais (contador, sistema folha, treinamentos).
          Provisão de multa FGTS calculada sobre saldo anual acumulado (40%).
          IRRF com isenção atualizada pela Lei nº 15.191/2025 para quem ganha
          até R$ 3.036,00. Para cálculos específicos da sua empresa, consulte um
          contador.
        </p>
      </div>
    </MainCard>
  );
};
